import React, { useEffect, useRef, useState } from "react";
import Navbar from "../Navbar/Navbar";
import Drafts, { Draft, mapToDarft } from "./Drafts";
import Feedback, { FeedbackType, mapToFeedback } from "./Feedback";
import Published, { mapToPublish, Publish } from "./Published";
import "./Stories.scss";
import Cookies from "js-cookie";
import config from "../../config/apiConfig";
import Loader from "react-spinners/RingLoader";
import { getInfo, useQuery } from "../../utils/hooks";
import ReactGA from "react-ga";
import { Route, Switch } from "react-router-dom";
import ProducerNav from "../ProducerNav/ProducerNav";

type StoryState = {
    drafts: Draft[];
    published: Publish[];
    feedbacks: FeedbackType[];
};

const Write: React.FC = () => {
    const query = useQuery();
    const tab = query.get("tab");
    const [menu, setMenu] = useState<number>(tab !== null ? parseInt(tab) : 0);
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState<StoryState>({
        drafts: [],
        published: [],
        feedbacks: [],
    });

    useEffect(() => {
        const formData = new FormData();
        formData.append("session-token", Cookies.get("token") ?? "");
        formData.append("token", config.token);
        const requestParams = {
            method: "POST",
            body: formData,
        };
        setIsLoading(true);
        fetch(config.BASE_URL + "/v1/get/writingtool/story/all", requestParams)
            .then((res) => res.json())
            .then((res) => {
                const drafts =
                    (
                        res.data.draft as { [key: string]: any }[] | undefined
                    )?.map((d) => mapToDarft(d)) ?? [];
                const published =
                    (
                        res.data.published as
                            | { [key: string]: any }[]
                            | undefined
                    )?.map((p) => mapToPublish(p)) ?? [];
                const feedbacks =
                    (res.data.published as { [key: string]: any }[] | undefined)
                        ?.filter((p) => p.number_of_reviews > 0)
                        ?.map((p) => mapToFeedback(p)) ?? [];
                setState({ drafts, published, feedbacks });
            })
            .finally(() => setIsLoading(false));
    }, []);

    const handleDelete = (id: string) => {
        if (!window.confirm("Are you sure?")) return;
        const formData = new FormData();
        formData.append("session-token", Cookies.get("token") ?? "");
        formData.append("token", config.token);
        const requestParams = {
            method: "POST",
            body: formData,
        };
        setIsLoading(true);
        fetch(
            config.BASE_URL + "/v1/put/writingtool/story/" + id + "/deleted",
            requestParams
        )
            .then(() => {
                const drafts = [...state.drafts.filter((d) => d.id !== id)];
                setState((s) => ({ ...s, drafts }));
                ReactGA.event({
                    category: "My Stories",
                    action: "Delete story",
                    label: id,
                });
            })
            .finally(() => setIsLoading(false));
    };

    const handlePublish = (id: string) => {
        const formData = new FormData();
        formData.append("session-token", Cookies.get("token") ?? "");
        formData.append("token", config.token);
        const requestParams = {
            method: "POST",
            body: formData,
        };
        setIsLoading(true);
        fetch(
            config.BASE_URL + "/v1/put/writingtool/story/" + id + "/published",
            requestParams
        )
            .then(() => {
                const draftToPublish = state.drafts.find((d) => d.id === id);
                if (!!!draftToPublish) return;
                const newPublished: Publish = {
                    id: draftToPublish.id,
                    avatar: draftToPublish.avatar,
                    title: draftToPublish.title,
                    updatedAt: new Date(),
                    comments: 0,
                    views: 0,
                };
                const drafts = [...state.drafts.filter((d) => d.id !== id)];
                const published = [newPublished, ...state.published];
                setState((s) => ({ ...s, drafts, published }));
                ReactGA.event({
                    category: "My Stories",
                    action: "Publish story",
                    label: id,
                });
            })
            .finally(() => setIsLoading(false));
    };

    const handleDownloadReport = (id: string) => {
        ReactGA.event({
            category: "My Stories",
            action: "Download report",
            label: id,
        });
        window.open(`/${getInfo()?.intent}/report/${id}`, "_blank")?.focus();
    };

    function getItem(): JSX.Element {
        switch (menu) {
            case 0:
                return (
                    <Drafts
                        drafts={state.drafts}
                        publish={handlePublish}
                        delete={handleDelete}
                    />
                );
            case 1:
                return <Published published={state.published} />;
            case 2:
                return (
                    <Feedback
                        feedback={state.feedbacks}
                        onDownload={handleDownloadReport}
                    />
                );
        }
        return (
            <Drafts
                drafts={state.drafts}
                publish={handlePublish}
                delete={handleDelete}
            />
        );
    }

    return (
        <div className="stories">
            <Switch>
                <Route path="/producer/story/my" component={ProducerNav} />
                <Route path="/writer/story/my" component={Navbar} />
            </Switch>
            <div className="header">
                <h2>All your stories</h2>
                <div className="tabbar-view">
                    <div
                        className={`tab ${menu == 0 ? "active" : ""}`}
                        onClick={() => setMenu(0)}
                    >
                        Drafts({state.drafts.length})
                    </div>
                    <div
                        className={`tab ${menu == 1 ? "active" : ""}`}
                        onClick={() => setMenu(1)}
                    >
                        Published({state.published.length})
                    </div>
                    <div
                        className={`tab ${menu == 2 ? "active" : ""}`}
                        onClick={() => setMenu(2)}
                    >
                        Feedback({state.feedbacks.length})
                    </div>
                    <div
                        className="tab-indicator"
                        style={{ transform: `translateX(${menu * 130}px)` }}
                    ></div>
                </div>
            </div>
            <div className="body">
                <div className="items-wrapper">
                    {isLoading ? <Loader color="pink" /> : getItem()}
                </div>
            </div>
        </div>
    );
};

export default Write;
